import BigNumber from "bignumber.js";
import { exactGetSwap, ISwap, ISwapPathInfo } from "../../../../api/swap";
import { useChains } from "../../../../components/FetchConfig";
import { useAlerting, useTranslation } from "../../../../hooks";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectProfile } from "../../../../reducer/profileSlice";
import {
  amountToCryptoAmount,
  findDecimalByChainNameAndAsset,
} from "../../../../utils/helper";
import { toQuoteDetailsAmount } from "../Content/components/QuoteDetails";
import { getApiSwapPath } from "../Content/helpers/getApiSwapPath";
import { useCreateSwapRequestContext } from "../context";
import { useSwap } from "./useSwap";
import { getCommonDisplayDecimals } from "../helpers/getCommonDisplayDecimals";
import { getTronSmartAccountType } from "../../helpers/getTronSmartAccountType";

interface IProps {
  swapPaths: ISwapPathInfo[];
}

export const useExactGetSwap = (props: IProps) => {
  const { swapPaths } = props;

  const { filterFields, exactGetFields, setExactGetFields, resetQuoteDetails } =
    useCreateSwapRequestContext();
  const { t } = useTranslation("createSwapRequest");
  const { alerting } = useAlerting();

  const { selectChainByPieces } = useChains();
  const { merchantId } = useAppSelector(selectProfile);
  const { chain_type, chain_id } = selectChainByPieces({
    chain_name: filterFields.chainName,
  });

  const fromCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.fromCurrency
      )
    ) || 6;

  const toCurrencyDecimals =
    Number(
      findDecimalByChainNameAndAsset(
        String(chain_type),
        String(chain_id),
        filterFields.toCurrency
      )
    ) || 6;

  const validateExactGetFields = (): boolean => {
    if (!filterFields.fromCurrency || !filterFields.toCurrency) {
      alerting("warning", t("psCurrencyPair"));
      return false;
    }

    if (!exactGetFields.walletAddress) {
      alerting("warning", t("psWalletAddress"));
      return false;
    }

    if (!exactGetFields.exactGetAmount) {
      alerting("warning", t("peExactGetAmount"));
      return false;
    }

    if (!exactGetFields.estimateAmount || !exactGetFields.payMax) {
      alerting("warning", t("plzQuoteFist"));
      return false;
    }

    return true;
  };

  const swapPayload: ISwap = {
    chain_type: Number(chain_type),
    chain_id: Number(chain_id),
    asset_name: filterFields.toCurrency,
    asset_name_pay: filterFields.fromCurrency,
    smart_account_type: getTronSmartAccountType(
      Number(chain_type),
      filterFields.isTronRent
    ),
    orders: [
      {
        amount: amountToCryptoAmount(
          BigNumber(exactGetFields.exactGetAmount)
            .decimalPlaces(getCommonDisplayDecimals(toCurrencyDecimals), 1)
            .toFixed(),
          toCurrencyDecimals
        ),
        decimals: toCurrencyDecimals,

        from_address: exactGetFields.walletAddress,
        to_address: exactGetFields.walletAddress,

        amount_pay_est: amountToCryptoAmount(
          toQuoteDetailsAmount(
            exactGetFields.estimateAmount,
            fromCurrencyDecimals
          ),
          fromCurrencyDecimals
        ),
        amount_pay_max: amountToCryptoAmount(
          toQuoteDetailsAmount(exactGetFields.payMax, fromCurrencyDecimals),
          fromCurrencyDecimals
        ),
        decimals_pay: fromCurrencyDecimals,

        max_slippage: Number(filterFields.maxSlippage) * 100000,
        swap_path: getApiSwapPath(
          swapPaths,
          filterFields.fromCurrency,
          filterFields.toCurrency
        ),
      },
    ],
  };

  const { handleSwap } = useSwap({
    resetAmount: () => {
      setExactGetFields({
        ...exactGetFields,
        exactGetAmount: "",
      });
      resetQuoteDetails();
    },
    validateFields: validateExactGetFields,
    swapApiFunc: exactGetSwap,
    swapPayload: swapPayload,
  });

  return { handleSwap };
};
